import { Accordion, Alert, Loader, Stack, Text, Title } from '@mantine/core';
import React from 'react';
import { ImportHistoryStatus } from '../../../models/employeeHub';
import { useImportHistory } from '../hooks/useImportHistory';
import { ImportResultSummary } from './ImportResultSummary';

const formatImportedAt = (importedAt: string): string => {
    const date = new Date(importedAt);
    // Reports saved before the history envelope existed deserialize with a default/empty date.
    if (Number.isNaN(date.getTime()) || date.getUTCFullYear() <= 1) {
        return 'Unknown date';
    }
    return date.toLocaleString();
};

export const ImportHistoryPanel: React.FC = () => {
    const { data, isLoading, isError } = useImportHistory();

    return (
        <Stack gap="xs">
            <Title order={4} size="h5" fw={600}>
                Recent Imports
            </Title>

            {isLoading && <Loader size="sm" />}

            {isError && (
                <Alert color="red" title="Could not load import history">
                    An unexpected error occurred while contacting the server. Please try again.
                </Alert>
            )}

            {data?.status === ImportHistoryStatus.StorageNotConfigured && (
                <Alert color="gray" title="Import history unavailable">
                    No storage account/container is configured for this environment — past import reports are not retained.
                </Alert>
            )}

            {data?.status === ImportHistoryStatus.StorageUnavailable && (
                <Alert color="red" title="Import history unavailable">
                    Could not reach the configured storage account/container.
                </Alert>
            )}

            {data?.status === ImportHistoryStatus.Available && data.entries.length === 0 && (
                <Alert color="gray" title="No import history available">
                    No previous imports have been recorded yet.
                </Alert>
            )}

            {data?.status === ImportHistoryStatus.Available && data.entries.length > 0 && (
                <Accordion variant="contained">
                    {data.entries.map((entry, index) => (
                        <Accordion.Item key={`${entry.importedAt}-${index}`} value={`entry-${index}`}>
                            <Accordion.Control>
                                <Text fw={500}>
                                    {formatImportedAt(entry.importedAt)}
                                </Text>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <ImportResultSummary result={entry.result} />
                            </Accordion.Panel>
                        </Accordion.Item>
                    ))}
                </Accordion>
            )}
        </Stack>
    );
};
